import React, { useCallback } from 'react';
import styled from 'styled-components';
import {
  LANDING_COLUMN_WIDTH2,
  LANDING_COLUMN_WIDTH2_MOBILE,
  LANDING_COLUMN_GAP2,
  type LandingArchiveCandidate,
  type LandingArchiveGroup,
} from '../../shared/util/landingArchiveIndex';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { DeviceType } from '../../shared/enum/EnumRepository';

export interface LandingArchiveIndexProps {
  groups: LandingArchiveGroup[];
  onCandidateActivate: (candidate: LandingArchiveCandidate) => void;
  onCandidatePreviewChange: (candidate: LandingArchiveCandidate | null) => void;
}

const LandingArchiveIndex: React.FC<LandingArchiveIndexProps> = ({
  groups,
  onCandidateActivate,
  onCandidatePreviewChange,
}) => {
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;

  const handleCandidateClick = useCallback(
    (e: React.MouseEvent, candidate: LandingArchiveCandidate) => {
      e.stopPropagation();
      onCandidateActivate(candidate);
    },
    [onCandidateActivate]
  );

  const renderCandidateItem = (
    candidate: LandingArchiveCandidate,
    key: string
  ) => (
    <CandidateItem
      key={key}
      $isMobile={isMobile}
      $isInteractive={Boolean(candidate.aui?.trim())}
      onClick={(e) => handleCandidateClick(e, candidate)}
      role={candidate.aui?.trim() ? 'button' : 'presentation'}
      tabIndex={candidate.aui?.trim() ? 0 : -1}
      onFocus={() => onCandidatePreviewChange(candidate)}
      onBlur={() => onCandidatePreviewChange(null)}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          onCandidatePreviewChange(candidate);
        }
      }}
      onPointerLeave={() => onCandidatePreviewChange(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onCandidateActivate(candidate);
        }
      }}
    >
      <CandidateUsername $isMobile={isMobile}>{candidate.username}</CandidateUsername>
    </CandidateItem>
  );

  return (
    <CandidateContainer data-mobile={isMobile ? 'true' : 'false'}>
      <ColumnList $isMobile={isMobile}>
        {groups.map((group) => (
          <GroupSection key={group.letter}>
            {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
            {group.candidates.map((candidate, candidateIndex) =>
              renderCandidateItem(
                candidate,
                `${candidate.handle}-${candidate.username}-${candidateIndex}`
              )
            )}
          </GroupSection>
        ))}
      </ColumnList>
    </CandidateContainer>
  );
};

const CandidateContainer = styled.div`
  width: 100%;
  max-width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ColumnList = styled.div<{ $isMobile: boolean }>`
  height: 100%;
  width: ${({ $isMobile }) =>
    ($isMobile ? LANDING_COLUMN_WIDTH2_MOBILE : LANDING_COLUMN_WIDTH2)};
  flex-shrink: 0;
  column-count: 1;
  column-gap: ${LANDING_COLUMN_GAP2};
  /* 주어진 높이를 채우면 다음 열로 이어서 배치합니다. */
  column-fill: auto;
`;

const GroupSection = styled.section`
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
  break-inside: avoid;
`;

const GroupLabel = styled.div`
  width: 100%;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  line-height: 1;
  break-after: avoid;
`;

const CandidateItem = styled.div<{ $isMobile: boolean; $isInteractive: boolean }>`
  width: 100%;
  min-width: ${({ $isMobile }) => ($isMobile ? 0 : 'auto')};
  display: flex;
  align-items: center;
  break-inside: avoid;
  cursor: ${({ $isInteractive }) => ($isInteractive ? 'pointer' : 'default')};

  &:hover > div {
    transform: scale(0.95);
  }
`;

const CandidateUsername = styled.div<{ $isMobile: boolean }>`
  padding: 3px 0;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  transform-origin: left center;
  transition: transform 0.25s ease-in-out;
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `
      : `
    width: clamp(100px, 50%, 180px);
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default LandingArchiveIndex;
